import { ContentNode, Query } from "@/app/graphql/codegen";
import { Grid2, Typography } from "@mui/material";
import { FETCH_DOCUMENTS_LIST } from "@documents/graphql/queries/fetchDocumentsList";
import { useDocumentSelectionSlice } from "@store/slices/documentSelection";
import { useViewSlice } from "@store/slices/view";
import { filterAndSortDocuments } from "@/utils/documentFilters";
import { useQuery } from "@apollo/client";
import DocumentTreeView from "../../../chat/components/sidebar/documents/DocumentTreeView";
import { useDispatch } from "react-redux";
import { setListSelection } from "@store/slices/documentSelection";
import LexiconDocumentFilter from "./LexiconDocumentFilter";
import { useSearchParams } from "react-router-dom";
import { useEffect, useMemo } from "react";

export const LexiconList = () => {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    
    // Support multiple parameter names for backward compatibility
    const documentId = searchParams.get("documentId") || searchParams.get("document_id");

    const { data, loading } = useQuery<Pick<Query, "officialDocuments">>(FETCH_DOCUMENTS_LIST);
    const docs = data?.officialDocuments ?? [];

    const { listId, sortOrder, sortField, searchTerm, jurisdiction } = useDocumentSelectionSlice();
    const { showExtendedView } = useViewSlice();

    // Apply filters to docs with memoization to prevent excessive re-computation
    const filteredDocs = useMemo(() => {
        return filterAndSortDocuments(docs, {
            searchTerm: searchTerm || "",
            jurisdiction: jurisdiction || undefined,
            sortField: sortField || undefined,
            sortOrder: sortOrder || undefined,
        });
    }, [docs, searchTerm, jurisdiction, sortField, sortOrder]);

    const skeletonHeight = 42;

    const selectDocFromNode = (node: ContentNode) => {
        const id = node?.associatedOfficialDocument?.id;
        if (id) {
            dispatch(setListSelection(id));
            // Also update the URL with the selected document ID
            setSearchParams({ documentId: id }, { replace: true });
        }
    };

    // Set the document selection from URL param when the document list loads
    useEffect(() => {
        if (documentId && docs.length > 0 && documentId !== listId) {
            console.log("Setting document selection from URL param:", documentId);
            // Only set selection if the document exists in our list
            const docExists = docs.some(doc => doc.id === documentId);
            if (docExists) {
                dispatch(setListSelection(documentId));
            } else {
                console.warn("Document ID from URL not found in document list:", documentId);
            }
        }
    }, [documentId, docs, dispatch, listId]);

    return (
        <Grid2 container size={12} p={1}>
            <DocumentTreeView 
                lexiconMode={true} 
                isGraphEnabled={false} 
                mainOnClick={selectDocFromNode}
                documents={filteredDocs} 
            />
        </Grid2>
    );
};
